/**
 * Spacing tokens from the Figma "Primitives" collection.
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

export const spacingKeys = ['xxs', 'xs', 's', 'sm', 'm', 'lg', 'xl', 'xxl', 'xxxl'] as const;
export type SpacingToken = (typeof spacingKeys)[number];

/**
 * The subset a SERVED document may name.
 *
 * `Stack`'s `gap` / `padding` / `paddingVertical` / `paddingHorizontal` and
 * `Screen`'s three padding props render this as a `z.enum`, so every installed
 * client carries its own frozen copy of it. A key added HERE is a value an older
 * copy does not know, which fails that client's strict catalog check and takes
 * the whole served document with it (ADR-118 Rule 2) — the same fleet event as a
 * new value on any other shipped enum.
 *
 * Adding a token to `spacing` above is free: it is a code constant a component
 * reads directly. Adding it to this list is not. `xxxl` is deliberately absent —
 * it exists for a component-internal inset, and no document needs to name it.
 */
export const servedSpacingKeys = [
  'xxs',
  'xs',
  's',
  'sm',
  'm',
  'lg',
  'xl',
  'xxl',
] as const satisfies readonly SpacingToken[];
export type ServedSpacingToken = (typeof servedSpacingKeys)[number];

export const spacing = {
  xxs: 2,
  xs: 4,
  s: 8,
  sm: 12,
  m: 16,
  lg: 24,
  xl: 32,
  xxl: 40,
  xxxl: 52,
} as const satisfies Record<SpacingToken, number>;

export type Spacing = typeof spacing;

/**
 * The default screen-edge gutter — the single owner of that value.
 *
 * `Screen` applies it as its `contentPaddingHorizontal` / `footerPaddingHorizontal`
 * default, and every component that cancels it to full-bleed (`Stack`, `Rail`,
 * `ScrollableWeekStrip` — see the ADR-020 Rule 2 carve-out) reads it from
 * here. It lives in the leaf `elements/` layer so the producer and all the
 * consumers share one constant instead of holding component-local copies in
 * sync by convention.
 *
 * This is the *card* margin (ADR-091): a card carries its own inner padding, so
 * a card stack at the wider value reads doubly inset. Text and UI that does not
 * live in a card takes SCREEN_GUTTER_WIDE instead.
 */
export const SCREEN_GUTTER_KEY = 'm' satisfies keyof Spacing;
export const SCREEN_GUTTER = spacing[SCREEN_GUTTER_KEY];

/**
 * The screen-edge gutter for text and UI that does not live in a card (ADR-091).
 *
 * Opt-in and always explicit at the render site — a screen passes it to `Screen`
 * rather than any component defaulting to it. Bleed cancels deliberately do NOT
 * read this: a full-bleed surface cancels the gutter its host applies, which is
 * SCREEN_GUTTER on every current host.
 */
export const SCREEN_GUTTER_WIDE_KEY = 'lg' satisfies keyof Spacing;
export const SCREEN_GUTTER_WIDE = spacing[SCREEN_GUTTER_WIDE_KEY];

/**
 * The extra inset that lands bare (un-carded) text on the wide gutter while its
 * host screen keeps the card gutter.
 *
 * A screen whose majority is cards keeps SCREEN_GUTTER at the Screen level
 * (ADR-091 Rule 1); the text that sits between those cards still belongs on
 * SCREEN_GUTTER_WIDE. The difference is applied as internal padding by the
 * component that renders the text (ADR-091 Rule 7), so it is derived from the
 * two gutters here and never restated as a literal.
 */
export const UNCARDED_TEXT_INSET = SCREEN_GUTTER_WIDE - SCREEN_GUTTER;
